import classNames from 'classnames';
import { useField, useFormikContext } from 'formik';
import type React from 'react';
import { forwardRef, useEffect } from 'react';
import type { Colors } from '../../../constants';
import { TextCaption } from '../../Typography';
import { InputTags } from '../InputTags';
import { statusClasses } from '../InputTextField';
import { RequiredAsterisk } from '../RequiredAsterix';

export interface InputTagsFieldProps {
  name: string;
  initialValue: string[];
  placeholder: string;
  className?: string;
  maxHeight?: number;
  inputSize?: 'sm' | 'md' | 'lg';
  label?: string;
  required?: boolean;
}

const labelClasses = {
  sm: 'text-ds-natural-300',
  md: 'text-ds-accent2-500',
  lg: 'text-ds-danger-400',
};

export const InputTagsField = forwardRef(
  (
    {
      name,
      initialValue,
      placeholder,
      maxHeight,
      label,
      inputSize = 'md',
      required,
    }: InputTagsFieldProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const [field, meta, helpers] = useField(name);
    const { setFieldValue } = useFormikContext();
    const isTouched = meta.touched;
    const errorString =
      typeof meta.error === 'object'
        ? (meta.error as any).find?.((err) => err)
        : meta.error;
    const hasError = meta.error;
    const inputState = hasError
      ? 'error'
      : isTouched && meta.value?.length > 0
        ? 'success'
        : 'normal';
    const errorIndexes =
      typeof meta.error === 'object'
        ? (meta.error as any).reduce(
            (filtered, currentValue, currentIndex, _array) => {
              if (currentValue) {
                filtered.push(currentIndex.toString());
              }
              return filtered;
            },
            []
          )
        : [];
    const success = isTouched && !hasError;
    const handleNewTag = (tags) => {
      setFieldValue(field.name, tags, true);
      if (!isTouched) {
        helpers.setTouched(true);
      }
    };
    const handleRemoveTag = (tags) => {
      if (!isTouched) {
        helpers.setTouched(true);
      }
      setFieldValue(field.name, tags, true);
    };
    useEffect(() => {
      helpers.setValue(initialValue);
    }, []);

    return (
      <div className={classNames('flex flex-col relative w-full')}>
        {label && (
          <div className={classNames(labelClasses[inputSize])}>
            <label htmlFor={field.name}>
              <TextCaption
                label={label}
                color={labelClasses[inputState]}
              />
            </label>
            {required && <RequiredAsterisk />}
          </div>
        )}
        <InputTags
          ref={ref}
          success={success}
          errorIndexes={errorIndexes}
          onNewTag={handleNewTag}
          onRemoveTag={handleRemoveTag}
          name={field.name}
          tags={field.value || []}
          placeholder={placeholder}
          maxHeight={maxHeight}
        />
        {hasError && isTouched && (
          <div className={classNames('mx-1 -bottom-5 absolute w-full')}>
            <div className="whitespace-nowrap">
              <TextCaption
                label={errorString}
                color={statusClasses[inputState] as Colors}
                lineHeight="leading-ds-100"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

InputTagsField.displayName = 'InputTagsField';
